import { FunctionComponent, useEffect, useState, useRef } from "react";
import { addNoteFirebase, createTaskFirebase, fetchConstants } from "../../../Services/contacts";
import { connect, useDispatch } from "react-redux";
import { showSnackbarAction } from "../../../Redux/actions";
import { useHistory } from "react-router-dom";
import moment from "moment";
import TextInput from "../../TextInput/InputText";
import { themeColors } from "../../theme";
import axios from "axios";
import { url } from "../../../Values/constants";
import "../../../theme-vars.css";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import Modal, { Styles } from "react-modal";
import StyledCancelButton from "../../Buttons/StyledCancelButton";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";
import { Circles } from "react-loader-spinner";
import { getLocation } from "../../../Values/utils";
import DropDown from "../../../Screens/LeadDistributorLogic/Dropdown"
import InputTextDate from "../../TextInput/InputTextDate";

// -------------------------------------------------------------
// Props Interface
// -------------------------------------------------------------
type props = {
  contactDetail: any;
  close: () => void;
  open: boolean;
  tasksData: any[];
  user: any;
  notes: any[];
  theme: any;
  authToken: any;
};

// -------------------------------------------------------------
// MAIN COMPONENT
// -------------------------------------------------------------
const CreateModal: FunctionComponent<props> = ({
  contactDetail,
  close,
  open,
  tasksData,
  user,
  notes,
  theme,
  authToken
}) => {

  // UI theme / media
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

  // Redux dispatcher / navigation
  const dispatcher = useDispatch();
  const history = useHistory();

  // Refs
  const noteRef: any = useRef();

  // Local States
  const [load, setLoad] = useState(false);
  const [nextFollowDate, setNextFollowDate] = useState<any>('');
  const [nextFollowSelected, setNextFollowSelected] = useState<any>({ label: "Select", value: "Select" });

  // Dropdown lists
  const [propertyTypeList, setPropertyTypeList] = useState<any[]>([]);
  const [propertyStageList, setPropertyStageList] = useState<any[]>([]);
  const [nextFollowUpList, setnextFollowUpList] = useState<any[]>([]);
  const [notIntReasonList, setNotIntReasonList] = useState<any[]>([]);
  const [lostReasonList, setLostReasonList] = useState<any[]>([]);

  // Geo location
  const [latitude, setLatitude] = useState<any>(null);
  const [longitude, setLongitude] = useState<any>(null);

  // Existing task handling
  const [existingTaskSelected, setExistingTaskSelected] = useState<any>({ label: "Select", value: "Select" });
  const [existingTaskStatus, setExistingTaskStatus] = useState(false);
  const [tasksListsData, setTasksListsData] = useState<any[]>([]);

  // -------------------------------------------------------------
  // Fetch constant values (reasons, stages, follow-up types)
  // Establish existing task completion requirement
  // -------------------------------------------------------------
  useEffect(() => {
    const unsubConst = fetchConstants(
      (data) => setNotIntReasonList(data),
      (data) => setLostReasonList(data),
      (data) => setPropertyStageList(data),
      (data) => setPropertyTypeList(data),
      (data) => setnextFollowUpList(data)
    );
    // If there is a pending task and it's not "Call Back", require closing it
    if (
      tasksData[0].status === "Pending" &&
      tasksData[0].type !== "Call Back"
    ) {
      setExistingTaskStatus(true);
    }
    return () => {
      unsubConst();
    };
  }, []);

  // -------------------------------------------------------------
  // Fetch user's location (lat & long)
  // -------------------------------------------------------------
  useEffect(() => {
    (async () => {
      try {
        const position = await getLocation();
        if (position) {
          setLatitude(position.latitude);
          setLongitude(position.longitude);
        }
      } catch (error) {
        console.error('Failed to fetch location:', error);
      }
    })();
  }, []);

  // -------------------------------------------------------------
  // Fetch tasks for this lead from backend when token is ready
  // -------------------------------------------------------------
  useEffect(() => {
    if (authToken) {
      getTasks();
    }
  }, [authToken])

  // Fetch all tasks for the customer
  const getTasks = async () => {
    const bodyData = {
      leadId: contactDetail.contactId,
    }
    axios.defaults.headers.common['x-access-token'] = authToken
    const getTasksRes = await axios.post(
      url + '/tasks/getTasksId',
      bodyData
    );
    setTasksListsData(getTasksRes?.data);
  }

  // -------------------------------------------------------------
  // Handle form submit
  // -------------------------------------------------------------
  const onSubmit = () => {
    if (nextFollowSelected.value === "Select") {
      dispatcher(
        showSnackbarAction(
          "Select Next Follow Up Type!!",
          "error"
        )
      );
    } else if (
      nextFollowDate === "" ||
      nextFollowDate < new Date()
    ) {
      dispatcher(
        showSnackbarAction("Enter A Valid Date!!", "error")
      );
    } else if (
      existingTaskStatus &&
      existingTaskSelected.value === "Select"
    ) {
      dispatcher(
        showSnackbarAction(
          "Select Exisiting Task Status!!",
          "error"
        )
      );
    } else {
      // -------------------------------------
      // If everything is valid, create new task
      // -------------------------------------
      setLoad(true);
      // Prepare task data
      const data = {
        due_date: moment(nextFollowDate).toDate(),
        type: nextFollowSelected.value,
        status: "Pending",
        customer_name: contactDetail.customer_name,
      };
      // Add note if typed
      if (noteRef.current.value.length !== 0) {
        addNoteFirebase(
          contactDetail.contactId,
          notes,
          noteRef.current.value,
          () => { },
          dispatcher,
          () => { },
          user.user_email
        );
      }

      // Create task
      createTaskFirebase(
        contactDetail.contactId,
        tasksData,
        data,
        {
          next_follow_up_type: nextFollowSelected.value,
          next_follow_up_date_time: moment(nextFollowDate).toDate(),
        },
        (value: boolean) => setLoad(value),
        dispatcher,
        user,
        close,
        history,
        existingTaskStatus &&
          existingTaskSelected.value === "Completed"
          ? true
          : false,
        latitude,
        longitude
      );
    }
    // Update unique meeting/site visit flags
    existingTaskStatus && existingTaskSelected.value === "Completed" && uniqueTaskUpdate()
  };

  // -------------------------------------------------------------
  // Update unique meeting or site visit logic if applicable
  // -------------------------------------------------------------
  const uniqueTaskUpdate = async () => {
    let unSiteVist = false;
    let unMeeting = false;

    // Check if unique meeting exists
    tasksListsData?.filter(item => item?.type === "Meeting")?.map((list: any) => {
      if (list?.unique_meeting === true) {
        return unMeeting = true;
      }
    });

    // Check if unique site visit exists

    tasksListsData?.filter(item => item?.type === "Site Visit")?.map((list: any) => {
      if (list?.unique_site_visit === true) {
        return unSiteVist = true;
      }
    });
    if (!unSiteVist && tasksListsData?.filter(item => item?.type === "Site Visit")?.some(list => list?.status === "Pending")) {
      const arrSiteMeetingList = tasksListsData?.filter(item => item?.type === "Site Visit" && item?.status === "Pending")
      const bodyData = {
        id: arrSiteMeetingList[0]?._id,
        unique_meeting: false,
        unique_site_visit: true
      }
      axios.defaults.headers.common['x-access-token'] = authToken
      const uniqueTaskTypeUpdate = await axios.post(
        url + '/tasks/uniqueTaskTypeUpdate',
        bodyData
      );
    }
    // Assign unique meeting if none exists
    if (!unMeeting && tasksListsData?.filter(item => item?.type === "Meeting")?.some(list => list?.status === "Pending")) {
      const arruniqueMeetingList = tasksListsData?.filter(item => item?.type === "Meeting" && item?.status === "Pending")
      const bodyData = {
        id: arruniqueMeetingList[0]?._id,
        unique_meeting: true,
        unique_site_visit: false
      }
      axios.defaults.headers.common['x-access-token'] = authToken
      const uniqueTaskTypeUpdate = await axios.post(
        url + '/tasks/uniqueTaskTypeUpdate',
        bodyData
      );
    }
  }

  // -------------------------------------------------------------
  // Modal Style
  // -------------------------------------------------------------
  const modalStyle: Styles = {
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 1300,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    content: {
      background: theme
        ? themeColors.cardBackgroundColor
        : themeColors.cardBackgroundColorDark,
      color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
      borderRadius: 16,
      padding: isMobile ? "24px 12px" : "40px 32px",
      width: 400,
      maxWidth: 400,
      border: "none",
      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
      inset: "auto",
      margin: "auto",
    },
  };

  // -------------------------------------------------------------
  // RENDER UI
  // -------------------------------------------------------------
  return (
    <>
      <Modal
        isOpen={open}
        onRequestClose={close}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        style={modalStyle}
        ariaHideApp={false}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 380,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <form style={{ width: "100%" }}>
            {/* Header */}
            <Box sx={{ mb: 2 }}>
              <h3
                style={{
                  color: theme ? "#272944" : "#fff",
                  fontFamily: "var(--font-family-main)",
                  fontSize: "var(--font-size-large)",
                  marginBottom: 24,
                  textAlign: "center",
                  fontWeight: 700,
                  letterSpacing: 0.5,
                }}
              >
                Create New Task
              </h3>
            </Box>
            {/* Form Fields */}
            <Grid container spacing={2} sx={{ width: "100%" }}>

              {/* Existing Task Status (if required) */}
              {existingTaskStatus && (
                <Grid item xs={12} sm={12}>
                  <DropDown
                    title="Existing Task Status"
                    option={["Completed", "Cancelled"]}
                    selectedValue={existingTaskSelected.value === "Select" ? "" : existingTaskSelected}
                    setSelectedValue={(value) => {
                      setExistingTaskSelected(value);
                    }}
                    required={true}
                  />
                </Grid>
              )}

              {/* Next Follow-up Type */}
              <Grid item xs={12} sm={12}>
                <DropDown
                  title="Next Follow Up Type"
                  option={nextFollowUpList}
                  selectedValue={nextFollowSelected.value === "Select" ? "" : nextFollowSelected}
                  setSelectedValue={(value) => {
                    setNextFollowSelected(value);
                  }}
                />
              </Grid>

              {/* Next Follow-up Date */}
              <Grid item xs={12} sm={12}>
                <InputTextDate
                  type="datetime-local"
                  title="Next Follow Up Date & Time"
                  value={nextFollowDate}
                  onChange={(e: any) => setNextFollowDate(e.target.value)}
                />
              </Grid>

              {/* Add Note */}
              <Grid item xs={12} sm={12}>
                <TextInput
                  title="Note"
                  style={{ backgroundColor: "#fff" }}
                  ref={noteRef}
                />
              </Grid>
            </Grid>

            {/* Buttons */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 4,
                  width: "100%",
                }}
              >
                <Box style={{ marginRight: 8 }}>
                  {/* Cancel */}
                  <StyledCancelButton onClick={close} isMobile={isMobile}>
                    Cancel
                  </StyledCancelButton>
                </Box>
                <Box>
                  {/* Submit */}
                  <StyledSubmitButton
                    onClick={onSubmit}
                    themeMode={themeMUI.palette.mode}
                    isMobile={isMobile}
                  >
                    {load ? (
                      <span style={{ display: "flex", justifyContent: "center" }}>
                        <Circles color="#fff" height={20} width={20} />
                      </span>
                    ) : (
                      <>Submit</>
                    )}
                  </StyledSubmitButton>
                </Box>
              </Box>
            </Grid>
          </form>
        </Box>
      </Modal >
    </>
  );
};

// -------------------------------------------------------------
// Redux: Connect State to Props
// -------------------------------------------------------------
const mapStateToProps = (state: any) => {
  return {
    user: state.user.data,
    theme: state.theme.isLightMode,
    authToken: state.user.authToken
  };
};

export default connect(mapStateToProps)(CreateModal);
